import { useMemo } from "react";
import { useLocation } from "react-router";
import DefaultImg from "../../../../assets/img/default-image.svg";
import Roles from "../../../../models/roles";
import { formatDate } from "../../../../models/vacancy";
import { AUTH_ROUTE } from "../../../../pages/App/routes";
import { useAppSelector as useSelector } from "../../../../store";
import {
  ITimesheetData,
  TTimesheetVariant,
} from "../../../../store/timesheet/types";
import { capitalizeName, extractSuburbFromAddress } from "../../../../utils/stringUtils";
import { getNextMonday } from "../../../../utils/timeHelper";
import CustomButton from "../../../UI/Buttons/CustomButton";
import CancelCircle from "../../../UI/Icons/CancelCircle";
import Eye from "../../../UI/Icons/Eye";
import PencilSmall from "../../../UI/Icons/PencilSmall";
import StarRating from "../../../UI/StarRating";
import ErrorMessage from "../../../UI/Tooltips/ErrorMessage";

const TableRow = ({
  variant = "client-pending",
  timesheet,
  showStatus = false,
  btnType = null,
  onReviewClick = () => {},
  onViewClick = () => {},
}: {
  variant: TTimesheetVariant;
  timesheet?: ITimesheetData;
  showStatus?: boolean;
  btnType?: "View" | "Review" | null;
  onReviewClick?: () => void;
  onViewClick?: () => void;
}) => {
  const [role, type] = variant.split("-") as [
    "client" | "admin",
    "pending" | "approval" | "history"
  ];

  const user = useSelector((state) => state.auth.user);
  const location = useLocation();

  const isAdmin = useMemo(
    () =>
      user?.role.name === Roles.SUPER_ADMIN ||
      user?.role.name === Roles.GENERAL_ADMIN,
    [user]
  );

  const isCentre = useMemo(
    () =>
      user?.role.name === Roles.CENTER_ADMIN ||
      user?.role.name === Roles.CENTER_USER,
    [user]
  );

  const isHistory = type === "history";
  const nextMonday = getNextMonday();

  const conditionForUnavailForWork = !isAdmin && variant === "client-history" && timesheet?.candidate?.is_documents_expired;

  const renderCandidateInfo = () => (
    <div className="d-flex gap-20 align-items-center">
      <div className="w-50px h-50px  border-2 border-grey-dark radius-40px">
        <img
          src={timesheet?.candidate?.photo || DefaultImg}
          alt="candidate"
          width={50}
          height={50}
          className="img-responsive rounded-circle object-fit-cover"
        />
      </div>
      <div className="d-flex flex-column gap-3 fs-16 text-black min-w-230px low-min-w-150px w-230px">
        <div className="fw-700">
          {capitalizeName(timesheet?.candidate?.first_name ?? "")} {capitalizeName(timesheet?.candidate?.last_name ?? "")}
        </div>
        <StarRating
          rating={
            role === "admin"
              ? timesheet?.rating?.average_rating || 0
              : timesheet?.rating?.latest_rating ||
                timesheet?.rating?.average_rating ||
                0
          }
          color={
            role === "admin" || !timesheet?.rating?.latest_rating
              ? "#aaaaaa"
              : "#51C2B9"
          }
          // color={"#51C2B9"}
        />
        {!(role === "admin") &&
          !timesheet?.rating?.latest_rating &&
          timesheet?.rating?.average_rating && (
            <div className="fs-10 fw-400 text-grey-dark">Average Rating</div>
          )}
        {conditionForUnavailForWork ? (
          <ErrorMessage className="align-items-center gap-3 lh-1" message="Unavailable for work" />
        ) : ""}
      </div>
    </div>
  );


  const renderCentreOrVacancyInfo = () => (
    <div className="d-flex flex-column gap-3 fs-16 text-black low-min-w-150px flex-grow-1">
      {variant === "client-approval" ? (
        <>
          <div className="fw-700">
            {timesheet?.vacancy?.matching_position?.name ||
              timesheet?.position_name}
          </div>
          <div className="fs-13"> 
            {timesheet?.centre?.business_name}, {timesheet?.centre?.address}
          </div>
        </>
      ) : (
        <>
          <div className="fw-700">
            {timesheet?.centre?.business_name},{" "}
            <span className="fw-400">{extractSuburbFromAddress(timesheet?.centre?.address || "")}</span>
          </div>
          <div className="fs-13">
            {timesheet?.vacancy?.matching_position?.name ||
              timesheet?.position_name}
          </div>
          <div className="fs-13">
            {
              (variant === "admin-history" || variant === "admin-pending") && timesheet?.payroll_approved_by && (
                <>Approved by {capitalizeName(timesheet?.approver?.first_name ?? "")} {capitalizeName(timesheet?.approver?.last_name ?? "")}, {formatDate(timesheet?.payroll_approved_on ? new Date(timesheet.payroll_approved_on) : null)} </>
              )
            }
          </div>
        </>
      )}
    </div>
  );

  const renderTimeframeInfo = () => (
    <div
      className={`d-flex flex-column ${
        variant === "client-approval"
          ? "min-w-210px low-min-w-120px"
          : "min-w-250px"
      } w-210px text-break low-min-w-150px`}
    >
      <div className="d-block d-lg-none fs-13 fw-700 text-black">
        Job Timeframe
      </div>
      <div className="fs-13 fw-400 text-black">{timesheet?.timeframe}</div>
      <div className="fw-700 fs-13 mt-2 d-flex gap-10 align-items-center">{timesheet?.did_not_work ? <><CancelCircle /> They did not work on this day</> : ""}</div>
    </div>
  );


  const renderStatus = () => {
    if (variant !== "client-approval") return null;

    return showStatus ? (
      <div className="d-flex gap-10 align-items-center justify-content-lg-center min-w-120px">
        <div className="d-block d-lg-none fs-13 fw-700 text-black">Status:</div>
        <div className="border border-alert radius-8px px-8px py-4px fs-13 fw-400 text-alert">
          Resubmitted
        </div>
      </div>
    ) : (
      <div className="d-flex gap-10 align-items-center justify-content-lg-center min-w-120px"></div>
    );
  };

  const renderAdminPayrollStatus = () => {
    if (variant === "client-approval") return null;
    if(!location.pathname.includes("admin/timesheet/payroll-pending")) return null;
    
    return  (
      <div className="d-flex gap-10 align-items-center  min-w-120px">
        <div className="d-block d-lg-none fs-13 fw-700 text-black">Status:</div>

        {
          !timesheet?.timesheet_status?.includes("Scheduled") && (
            <div className={`border border-alert radius-8px px-8px py-4px fs-13 text-alert ${(timesheet?.timesheet_status === "Overdue") ? "fw-700 bg-overdue-light-red" : "fw-400"} `}>
              {timesheet?.timesheet_status || ''}
            </div>
          )
        }

        {
          timesheet?.timesheet_status?.includes("Scheduled") && (
            <div className={`border border-grey-light radius-8px px-8px py-4px fs-13 text-grey-light fw-400 `}>
              {timesheet?.timesheet_status || ''}
            </div>
          )
        }
        


      </div>
    );
  };

  const renderActionButton = () => {
    if (!btnType) return null;
 
    return (
      <CustomButton
        variant={"primary"}
        type="button"
        className={`btn-theme ${(btnType === "View") ? "bg-off-white" : "bg-teal"} text-black w-100`}
        onClick={() => (btnType === "View" ? onViewClick() : onReviewClick())}
      >
        {btnType === "View" ? <Eye /> : <PencilSmall width={24} height={24} color="black"/>} {btnType}
      </CustomButton>
    );
  };

  const renderStatusForSuperAdminPending = () => {
    if (variant !== "admin-pending") return null;
    if(!location.pathname.includes("admin/timesheet/pending")) return null;
    return timesheet?.is_declined_timesheet ? (
      <div className="d-flex min-w-120px gap-10 align-items-center ">
        <div className="d-block d-lg-none fs-13 fw-700 text-black">Status:</div>
        <div className="border border-alert radius-8px px-8px py-4px fs-13 fw-400 text-alert">
          Declined
        </div>
      </div>
    ) : (
      <div className="d-flex min-w-120px gap-10 align-items-center">
        <div className="d-block d-lg-none fs-13 fw-700 text-black">Status:</div>
        <div className="border border-off-white radius-8px py-4px px-8px text-center text-grey-light fs-13 ">Pending</div>
      </div>
    );
  };

  return (
    <div className={`border-bottom border-off-white p-15px d-flex flex-column flex-lg-row gap-20 align-items-lg-center ${conditionForUnavailForWork ? "disabled-lineitem" : ""}`}>
      <div className="flex-grow-1 text-break d-flex flex-column flex-lg-row gap-20 gap-mobile-15 align-items-lg-center">

        
        {renderCandidateInfo()}
        <div className="flex-grow-1 d-flex flex-column flex-lg-row gap-20 align-items-lg-center">
          <div className="flex-grow-1 d-flex gap-20 align-items-lg-center">
            <div className="w-50px h-50px  border-2 border-grey-dark radius-40px">
              <img
                src={timesheet?.centre?.photo || DefaultImg}
                alt="centre"
                width={50}
                height={50}
                className="img-responsive rounded-circle object-fit-cover"
              />
            </div>
            <div className="flex-grow-1 d-flex flex-column flex-lg-row gap-20 gap-mobile-10 align-items-lg-center">
              {renderCentreOrVacancyInfo()}
              {renderTimeframeInfo()}
              {renderStatus()}
              {isAdmin && renderAdminPayrollStatus()}
             
              
              {/** It may need in future so don't delete the commented code */}
               {isAdmin  && variant !== "admin-history" && variant !== "admin-payroll-pending" && location.pathname.includes(AUTH_ROUTE.ADMIN_TIMESHEET_PENDING)  ? (
                <div className="min-w-120px w-120px">
                  { timesheet?.min_base_rate &&
                  timesheet.max_base_rate ? (
                    <div className="fs-13 text-black fw-400">
                      ${timesheet.min_base_rate}-$
                      {timesheet.max_base_rate}/hr{" "}
                      {timesheet?.allowance !== null ? (
                        <span className="fw-700 text-teal-dark">
                          + extra ${timesheet?.allowance}/day allowance
                        </span>
                      ) : (
                        ""
                      )}
                    </div>
                  ) : (
                    "-"
                  )}
                </div>
              ) : isAdmin && variant !== "admin-history" && variant !== "admin-payroll-pending" ? (
                <div className="min-w-120px w-120px">
                  {timesheet?.vacancy &&
                  timesheet.vacancy.matching_position?.min_base_rate &&
                  timesheet.vacancy.matching_position.max_base_rate ? (
                    <div className="fs-13 text-black fw-400">
                      ${timesheet?.vacancy?.matching_position.min_base_rate}-$
                      {timesheet?.vacancy?.matching_position.max_base_rate}/hr{" "}
                      {timesheet?.vacancy?.allowance !== null ? (
                        <span className="fw-700 text-teal-dark">
                          + extra ${timesheet?.vacancy?.allowance}/day allowance
                        </span>
                      ) : (
                        ""
                      )}
                    </div>
                  ) : (
                    "-"
                  )}
                </div>
              ) : (
                ""
              )}
 
               {variant === "admin-pending" && renderStatusForSuperAdminPending()}
             
              {
                isAdmin && variant === "admin-history" && (
                  <>
                    {
                      timesheet?.sent_to_payroll_on ? (
                        <div className="fs-13 text-black min-w-120px">Sent to Payroll <br /> {formatDate(timesheet?.sent_to_payroll_on ? new Date(timesheet.sent_to_payroll_on) : null)}</div>
                      ) : (
                        <div className="text-grey-medium fs-13 min-w-120px">Scheduled for <br /> {getNextMonday()}</div>
                      )
                    }
                  </>                  
                )
              }
            </div>
          </div>
        </div>
      </div>
      <div
        className={
          isHistory || variant === "admin-approval" || variant === "admin-pending"
            ? "min-w-110px"
            : "min-w-130px"
        }
      >
        {renderActionButton()}
      </div>
    </div>
  );
};

export default TableRow;
